import React from 'react';
import {Formik} from "formik";
import {Button, Col, Row} from "react-bootstrap";
import * as Yup from "yup";
import MySwal from "sweetalert2";
import {registerUser} from "../../../services/users";
import {NotificationManager} from "react-notifications";
import {languages, normalizeLanguage} from "../../../utils";
import {useTranslation} from "react-i18next";

// React Components
import Input from "../../../components/Input";
import ErrorMessage from "../../../components/ErrorMessage";
import InputSelect from "../../../components/InputSelect";

const RegistrationForm = (props) => {
   const {i18n, t} = useTranslation();

   return <div className={"RegistrationForm"}>
      <Row>
         <Col xs={10} sm={9} md={7} lg={5} className={"mx-auto mb-3"}>
            <h1 className={"text-white mb-1"}>{t('create_account_page.title')}</h1>
         </Col>
      </Row>

      <Row>
         <Col xs={10} sm={9} md={7} lg={5} className={"mx-auto"}>
            <Formik
               initialValues={{name: '', email: '', phoneNumber: '', language: normalizeLanguage(i18n.resolvedLanguage || i18n.language), password: '', passwordConfirmation: ''}}
               validationSchema={Yup.object({
                  name: Yup.string()
                  .required(t('common.form.errors.mandatory_field')),
                  email: Yup.string()
                  .required(t('common.form.errors.mandatory_field'))
                  .email(t('common.form.errors.invalid_email')),
                  phoneNumber: Yup.string()
                  .required(t('common.form.errors.mandatory_field'))
                  .min(9, t('common.form.errors.invalid_phone_number'))
                  .matches(/^[0-9]+$/, t("common.form.errors.special_characters")),
                  password: Yup.string()
                  .required(t('common.form.errors.mandatory_field'))
                  .min(8, t('common.form..errors.invalid_password')),
                  passwordConfirmation: Yup.string()
                  .required(t('common.form.errors.mandatory_field'))
                  .when('password', {
                     is: (password) => password !== "",
                     then: Yup.string()
                     .oneOf([Yup.ref('password')], t('common.form.errors.matching_passwords'))
                  })
               })}
               onSubmit={(values) => {
                  registerUser(values.email, values.name, values.password, values.phoneNumber, values.language).then(r => {
                     MySwal.fire({
                        heightAuto: false,
                        titleText: `${t('common.success')}!`,
                        text: t('create_account_page.successful_registration')
                     }).then((res) => {
                        props.setPage("login");
                     });
                  }).catch((e) => NotificationManager.error(e.data.errors.full_messages[0], t("common.error"), 4000));
               }}
            >
               {formik => (
                  <form onSubmit={formik.handleSubmit}>
                     <Input
                        id={"name"}
                        name={"name"}
                        inputType={"text"}
                        placeHolder={t('common.form.labels.name')}
                        inputTextValue={formik.values.name}
                        handleChange={formik.handleChange}
                        handleOnBlur={formik.handleBlur}
                     />
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"name"}/>

                     <Input
                        id={"email"}
                        name={"email"}
                        inputType={"email"}
                        placeHolder={t('common.form.labels.email')}
                        inputTextValue={formik.values.email}
                        handleChange={formik.handleChange}
                        handleOnBlur={formik.handleBlur}
                     />
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"email"}/>

                     <Input
                        id={"phoneNumber"}
                        name={"phoneNumber"}
                        inputType={"tel"}
                        placeHolder={t('common.form.labels.phone_number')}
                        inputTextValue={formik.values.phoneNumber}
                        handleChange={formik.handleChange}
                        handleOnBlur={formik.handleBlur}
                     />
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"phoneNumber"}/>

                     <InputSelect inputType="text">
                        <select
                           onChange={(event) => {
                              formik.handleChange(event);
                              i18n.changeLanguage(normalizeLanguage(event.target.value));
                           }}
                           onBlur={formik.handleBlur}
                           value={formik.values.language !== '' ? normalizeLanguage(formik.values.language) : 'DEFAULT'}
                           name="language"
                           className={"input-background language-selector" + (formik.values.language === '' ? " language-placeholder" : "")}
                        >
                           <option value="DEFAULT" disabled>{t('common.form.labels.language')}</option>
                           {Object.keys(languages).map((language) => {
                              return <option key={language} value={language}>{languages[language].nativeName}</option>
                           })}
                        </select>
                     </InputSelect>
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"language"}/>

                     <Input
                        id={"password"}
                        name={"password"}
                        inputType={"password"}
                        placeHolder={t('common.form.labels.password')}
                        inputTextValue={formik.values.password}
                        handleChange={formik.handleChange}
                        handleOnBlur={formik.handleBlur}
                     />
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"password"}/>

                     <Input
                        id={"passwordConfirmation"}
                        name={"passwordConfirmation"}
                        inputType={"password"}
                        placeHolder={t('common.form.labels.password_confirmation')}
                        inputTextValue={formik.values.passwordConfirmation}
                        handleChange={formik.handleChange}
                        handleOnBlur={formik.handleBlur}
                     />
                     <ErrorMessage errors={formik.errors} touched={formik.touched} name={"passwordConfirmation"}/>

                     <Button className={"w-100 mt-1 mb-3 login-button"} type="submit" variant={"primary"}>{t('create_account_page.register')}</Button>
                  </form>
               )}
            </Formik>
         </Col>
      </Row>

      <Row>
         <Col xs={12} className={"text-center mb-2"}>
            <p className={"links"} onClick={() => props.setPage("passwordRecovery")}>{t('login_page.forgotten_password')}</p>
         </Col>
         <Col xs={12} className={"text-center"}>
            <p className={"links"} onClick={() => props.setPage("login")}>{t('login_page.already_registered')}</p>
         </Col>
      </Row>
   </div>
};

export default RegistrationForm;
